import { useContext, useEffect, useMemo, useState } from 'react';

import {
    EntityContext,
    EntityIdentifier,
    EntityType,
} from 'context/entity/types';
import shallowEqual from 'shallowequal';

import { convertToCompatibleType } from 'utility/Convert/convertToCompatibleType';

interface Container {
    value: unknown;
}

function buildSelector<
    Identifier extends EntityIdentifier,
    Type extends EntityType
>(
    identifier: Identifier,
    selector?: (entityRecord: Type) => unknown
): (entityRecord: Record<Identifier, Type>) => unknown {
    if (selector) {
        return (entityRecord) => {
            const entity = entityRecord[identifier];

            return entity ? selector(entity) : entity;
        };
    } else {
        return (entityRecord) => entityRecord[identifier];
    }
}

export function createUseEntity<
    Identifier extends EntityIdentifier,
    Type extends EntityType
>(context: EntityContext<Identifier, Type>) {
    function useEntity(identifier: Identifier): Type;
    function useEntity<Selected>(
        identifier: Identifier,
        selector: (entityRecord: Type) => Selected
    ): Selected;

    function useEntity(
        identifier: Identifier,
        selector?: (entityRecord: Type) => unknown
    ) {
        const { getState, subscribe } = useContext(context);

        const internalSelector = useMemo(
            () => buildSelector(identifier, selector),
            [identifier, selector]
        );

        // we need to use `Container` because `unknown` leads to
        // `setState` having the inferred type `Dispatch<unknown>`
        // but we need `Dispatch<SetStateAction<unknown>>`, so we use `Container`
        // to have a concrete type with a `value` of arbitrary type
        // (btw, this happens with `any`, too)
        const [state, setState] = useState<Container>(() => ({
            value: internalSelector(getState()),
        }));

        useEffect(() => {
            return subscribe((entityRecord) => {
                setState((currentState) => {
                    const newState = internalSelector(entityRecord);
                    if (
                        shallowEqual(
                            convertToCompatibleType(currentState.value),
                            convertToCompatibleType(newState)
                        )
                    ) {
                        return currentState;
                    }

                    return { value: newState };
                });
            });
        }, [internalSelector, subscribe]);

        return state.value;
    }

    return useEntity;
}
